import { useStory } from './useStory';

/**
 * Fetches a global configuration story from Storyblok based its slug.
 *
 * For fetching any single arbitrary story, use `useStory`. For fetching the current config, use `useConfig`. For
 * fetching multiple stories, use `useStories`. For fetching datasource entries, use `useDatasourceEntries`.
 *
 * **Example:**
 * ```ts
 * import { useGlobalConfig } from '~/composables/storyblok-proxy/useGlobalConfig';
 *
 * const { data: story } = await useGlobalConfig<SubSectionRegistryConfigStoryblok>('section-registry', {
 *     resolve_relations: 'SubSectionRegistryConfig.subSectionConfigs'
 * });
 * ```
 *
 * @param slug - The slug of the global config story to fetch, without the app folder and `h__global_configs/` folder.
 * @param params - Optional Storyblok story parameters.
 * @returns A reactive object containing the fetched global config story data, loading state, and error state.
 * @throws Will throw an fatal error if the fetch fails.
 */
export const useGlobalConfig = <T>(slug: Ref<string> | string, params: Parameters<typeof useStory>[1]) => {
    const slugRef = toRef(slug);
    const _slug = computed(() => `${StoryblokStaticSlugs.GlobalConfigs}/${slugRef.value}`);

    return useStory<T>(_slug, params, {
        disableCountrySegment: true
    });
};
